<template>
  <div class="step-sessions">
    <div class="toolbar">
      <button type="button" class="btn-action" :disabled="!canManage" @click="openCreate">
        {{ $t('bo.programs.creation.addSession') }}
      </button>
      <button type="button" class="btn-action secondary" :disabled="!canManage" @click="openTemplatePicker">
        {{ $t('bo.programs.creation.importTemplate') }}
      </button>
    </div>

    <div v-if="store.isLoadingSessions" class="state">{{ $t('bo.common.loading') }}</div>
    <div v-else-if="!store.sessions.length" class="state empty">{{ $t('bo.programs.creation.noSessions') }}</div>

    <ul v-else class="session-list">
      <li v-for="session in store.sessions" :key="session.id" class="session-item">
        <div>
          <div class="session-name">{{ session.name }}</div>
          <div class="session-desc">{{ session.description }}</div>
        </div>
        <div class="session-actions">
          <button type="button" class="link-btn" :disabled="!canManage" @click="openEdit(session)">
            {{ $t('bo.common.edit') }}
          </button>
          <button type="button" class="link-btn danger" :disabled="!canManage" @click="confirmDelete(session)">
            {{ $t('bo.common.delete') }}
          </button>
        </div>
      </li>
    </ul>

    <div v-if="showForm" class="modal-overlay" @click.self="closeForm">
      <div class="modal">
        <h3>{{ editingSession ? $t('bo.programs.creation.editSession') : $t('bo.programs.creation.newSession') }}</h3>
        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelTitle') }} <span class="required">*</span></label>
          <input v-model="form.name" type="text" class="input" maxlength="200" />
        </div>
        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelDescription') }} <span class="required">*</span></label>
          <textarea v-model="form.description" class="textarea" rows="3" maxlength="2000" />
        </div>
        <div class="form-group">
          <label>{{ $t('bo.programs.creation.milestone') }}</label>
          <textarea v-model="form.milestone" class="textarea" rows="2" maxlength="500" />
        </div>
        <div class="form-group">
          <label>{{ $t('bo.sessionTemplates.labelDuration') }}</label>
          <select v-model.number="form.recommended_duration" class="input">
            <option :value="null">{{ $t('bo.programs.creation.chooseDuration') }}</option>
            <option v-for="d in durations" :key="d" :value="d">{{ d }} min</option>
          </select>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="closeForm">{{ $t('bo.common.cancel') }}</button>
          <button type="button" class="btn-primary" :disabled="store.isSubmitting || !form.description.trim()" @click="saveSession">
            {{ $t('bo.common.save') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showTemplates" class="modal-overlay" @click.self="showTemplates = false">
      <div class="modal">
        <h3>{{ $t('bo.programs.creation.pickTemplate') }}</h3>
        <div v-if="loadingTemplates" class="state">{{ $t('bo.common.loading') }}</div>
        <ul v-else class="template-list">
          <li v-for="tpl in templates" :key="tpl.id">
            <button type="button" class="template-btn" @click="importTemplate(tpl.id)">
              {{ tpl.title || tpl.name }}
            </button>
          </li>
        </ul>
        <div v-if="!loadingTemplates && !templates.length" class="state empty">{{ $t('bo.programs.creation.noTemplates') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useAdminProgramCreationStore } from '@/stores/adminProgramCreation'
import sessionTemplateService from '@/services/sessionTemplateService'

const store = useAdminProgramCreationStore()
const durations = [15, 20, 30, 45, 60, 90]

const showForm = ref(false)
const showTemplates = ref(false)
const editingSession = ref(null)
const templates = ref([])
const loadingTemplates = ref(false)

const form = reactive({
  name: '',
  description: '',
  milestone: '',
  recommended_duration: null,
})

const canManage = computed(
  () => store.programId != null && !store.isArchivedProgram
)

const resetForm = () => {
  form.name = ''
  form.description = ''
  form.milestone = ''
  form.recommended_duration = null
  editingSession.value = null
}

const openCreate = () => {
  resetForm()
  showForm.value = true
}

const openEdit = (session) => {
  editingSession.value = session
  form.name = session.name
  form.description = session.description
  form.milestone = session.milestone ?? ''
  form.recommended_duration = session.recommended_duration
  showForm.value = true
}

const closeForm = () => {
  showForm.value = false
  resetForm()
}

const saveSession = async () => {
  if (!form.description.trim()) return
  if (!form.name.trim()) form.name = form.description.slice(0, 100)
  const payload = { ...form }
  if (editingSession.value?.id) {
    await store.updateSession(editingSession.value.id, payload)
  } else {
    await store.createSession(payload)
  }
  closeForm()
}

const confirmDelete = async (session) => {
  if (!confirm(`Delete session "${session.name}"?`)) return
  await store.deleteSession(session.id)
}

const openTemplatePicker = async () => {
  showTemplates.value = true
  loadingTemplates.value = true
  try {
    const res = await sessionTemplateService.getTemplates({ status: 'published' })
    const data = res?.data ?? res
    templates.value = Array.isArray(data) ? data : data?.data ?? []
  } finally {
    loadingTemplates.value = false
  }
}

const importTemplate = async (templateId) => {
  await store.createSessionFromTemplate(templateId)
  showTemplates.value = false
}

onMounted(async () => {
  if (store.programId) await store.fetchSessions()
})
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.btn-action {
  padding: 0.45rem 0.9rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-action.secondary {
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.session-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.session-item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}

.session-name {
  font-weight: 600;
  color: #1f2937;
}

.session-desc {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.session-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.link-btn {
  border: none;
  background: none;
  color: #3730a3;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
}

.link-btn.danger {
  color: #dc2626;
}

.state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  padding: 1.25rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal h3 {
  margin: 0 0 1rem;
  font-size: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
}

.required {
  color: #dc2626;
}

.input,
.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.45rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: #3b82f6;
  border: none;
  color: white;
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
}

.template-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.template-btn {
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #f9fafb;
  cursor: pointer;
  margin-bottom: 0.5rem;
}
</style>
